import React from 'react';
import { motion } from 'framer-motion';
import {
  FaFacebook, FaTwitter, FaLinkedin, FaApple, FaGooglePlay
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import './Footer.css';

const Footer = () => {
  const footerLinks = {
    company: [
      'About Us', 'Careers', 'Blog', 'Press',
      'Partners', 'Security', 'Legal', 'Contact'
    ],
    resources: [
      'Help Center', 'FAQ', 'Community',
      'Developers', 'API Docs', 'Investors',
      'Tutorials', 'Research'
    ],
    legal: [
      'Privacy Policy', 'Terms of Service',
      'Cookie Policy', 'GDPR', 'Compliance',
      'Licenses', 'Disclosures', 'KYC Policy'
    ]
  };

  const socialLinks = [
    { icon: <FaFacebook />, label: 'Facebook' },
    { icon: <FaTwitter />,  label: 'Twitter'  },
    { icon: <FaLinkedin />, label: 'LinkedIn' }
  ];

  const appStores = [
    { icon: <FaApple />,     store: 'App Store'   },
    { icon: <FaGooglePlay />, store: 'Google Play' }
  ];

  const getLinkRoute = (link) => {
    const routeMap = {
      'About Us': '/about-us', 'Careers': '/careers', 'Blog': '/blog',
      'Press': '/press', 'Partners': '/partners', 'Security': '/security',
      'Legal': '/legal', 'Contact': '/contact-us', 'Help Center': '/help-center',
      'FAQ': '/faq', 'Community': '/community', 'Developers': '/developers',
      'API Docs': '/api-docs', 'Investors': '/verify', 'Tutorials': '/tutorials',
      'Research': '/research', 'Privacy Policy': '/privacy-policy',
      'Terms of Service': '/terms-and-services', 'Cookie Policy': '/cookie-policy',
      'GDPR': '/gdpr', 'Compliance': '/compliance', 'Licenses': '/license',
      'Disclosures': '/disclosure', 'KYC Policy': '/kyc-policy'
    };
    return routeMap[link] || '#';
  };

  return (
    <footer className="footer">
      <div className="footer-container">

        {/* ── Top section ── */}
        <div className="footer-top">

          {/* Left: brand + download */}
          <div className="footer-left-section">
            <div className="footer-brand">
              <motion.div
                className="footer-logo"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="white-logo-container">
                  <div className="logo-symbol">
                    <img
                      src="/Assets/logo.png"
                      alt="Rupayya Logo"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML =
                          '<span style="color:#0A0A0F;font-weight:800;font-size:1.4rem;">₹</span>';
                      }}
                    />
                  </div>
                </div>
                <h2>Rupayya</h2>
              </motion.div>
              <p className="footer-tagline">India's First Social Lending Infrastructure</p>
            </div>

            <div className="footer-download">
              <h4>Download Our App</h4>
              <div className="download-buttons">
                {appStores.map((store, i) => (
                  <motion.button
                    key={i}
                    className="store-btn"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    {store.icon}
                    <div>
                      <span>Get on</span>
                      <strong>{store.store}</strong>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          </div>

          {/* Right: links + newsletter */}
          <div className="footer-right-section">
            <div className="footer-links-grid">

              {Object.entries(footerLinks).map(([category, links], ci) => (
                <motion.div
                  key={category}
                  className="footer-column"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: ci * 0.08 }}
                >
                  <h3 className="column-title">
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </h3>
                  <ul className="column-links">
                    {links.map((link, li) => {
                      const route = getLinkRoute(link);
                      return (
                        <motion.li
                          key={link}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: li * 0.04 }}
                        >
                          {route !== '#'
                            ? <Link to={route} className="footer-link">{link}</Link>
                            : <span className="footer-link">{link}</span>
                          }
                        </motion.li>
                      );
                    })}
                  </ul>
                </motion.div>
              ))}

              {/* Newsletter column */}
              <motion.div
                className="footer-newsletter-column"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h3 className="column-title">Stay Updated</h3>
                <p className="newsletter-description">
                  Subscribe for product updates and fintech insights
                </p>
                <div className="newsletter-input">
                  <input type="email" placeholder="Enter your email" />
                  <button className="subscribe-btn">Subscribe</button>
                </div>

                <div className="newsletter-social-section">
                  <p className="follow-us-text">Follow us</p>
                  <div className="social-icons-single-line">
                    {socialLinks.map((social, i) => (
                      <motion.a
                        key={i}
                        href="#"
                        className="social-icon-small"
                        whileHover={{ scale: 1.15 }}
                        whileTap={{ scale: 0.9 }}
                        aria-label={social.label}
                      >
                        {social.icon}
                      </motion.a>
                    ))}
                  </div>
                </div>
              </motion.div>

            </div>
          </div>
        </div>

        {/* ── Bottom section ── */}
        <div className="footer-bottom">
          <div className="copyright-section">
            <p className="copyright-text">
              © 2026 A unit of Tyrannus Pvt Ltd. All rights reserved.
            </p>
            <p className="disclaimer">
              Rupayya is a financial technology company, not a bank.
              Banking services provided by partners.
            </p>
          </div>
          <div className="legal-links-center">
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/terms-and-services">Terms of Service</Link>
            <Link to="/cookie-policy">Cookie Policy</Link>
            <Link to="/license">Licenses</Link>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;
